import {useEffect, useLayoutEffect, useRef, useState} from 'react'
import {CHAPTER_ONE_END, TOTAL} from './demoState'

const useIsoLayout = typeof window !== 'undefined' ? useLayoutEffect : useEffect

export function useDemoClock() {
  const stageRef = useRef(null)
  const tRef = useRef(0)
  const pausedRef = useRef(false)
  const visibleRef = useRef(true)
  const hiddenRef = useRef(false)
  const lastRef = useRef(0)
  const [t, setT] = useState(0)
  const [paused, setPaused] = useState(false)
  const [scale, setScale] = useState(null)

  useIsoLayout(() => {
    const el = stageRef.current
    if (!el) return undefined
    const measure = () => {
      const width = el.clientWidth
      if (!width) return
      setScale(Math.min(1, width / 1440))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) {
      tRef.current = CHAPTER_ONE_END
      pausedRef.current = true
      setT(CHAPTER_ONE_END)
      setPaused(true)
    }

    lastRef.current = performance.now()
    const id = setInterval(() => {
      const now = performance.now()
      const dt = now - lastRef.current
      lastRef.current = now
      if (pausedRef.current || !visibleRef.current || hiddenRef.current) return
      const next = (tRef.current + dt) % TOTAL
      tRef.current = next
      setT(next)
    }, 60)

    const onVis = () => {
      hiddenRef.current = document.hidden
      lastRef.current = performance.now()
    }
    document.addEventListener('visibilitychange', onVis)

    const el = stageRef.current
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.boundingClientRect.height < 1) return
        visibleRef.current = entry.isIntersecting
        lastRef.current = performance.now()
      },
      {threshold: 0.2},
    )
    if (el) io.observe(el)

    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVis)
      io.disconnect()
    }
  }, [])

  const toggle = () => {
    pausedRef.current = !pausedRef.current
    lastRef.current = performance.now()
    setPaused(pausedRef.current)
  }

  const seek = ms => {
    tRef.current = ms
    lastRef.current = performance.now()
    setT(ms)
  }

  return {t, paused, scale, stageRef, toggle, seek}
}
